import React, { useEffect, useRef } from "react";
import { RIE_LieuNaissance_mask, RIE_LieuNaissance_restriction } from "utils/regex";
import { MaskInputConform, MaskInputConformProps } from "./MaskInput";
import { useTranslation } from "react-i18next";
import { debounce } from "utils/debounce";
import { verifyCodeInsee } from "api/codeInsee";
import { readError } from "utils/ErrorType";
import { ErrorParagraph } from "components/ErrorParagraph";
import { Box } from "@mui/material";

type LieuNaissanceInputProps = MaskInputConformProps & {
  onLieuNaissanceChecked?: (result: unknown) => void;
};

const isLieuNaissanceValid = (value: string) => {
  // Evite les effets de bord si la regex source possède le flag "g"
  const safeRegex = new RegExp(
    RIE_LieuNaissance_restriction.source,
    RIE_LieuNaissance_restriction.flags.replace("g", ""),
  );
  return safeRegex.test(value);
};

export const LieuNaissanceInput = ({
  label,
  field,
  onChange,
  ...props
}: LieuNaissanceInputProps) => {
  const { t } = useTranslation("common");

  const [error, setError] = React.useState<string | null>(null);

  const debouncedValidateCodeInsee = debounce(async (value: string) => {
    if (!value || !isLieuNaissanceValid(value)) {
      setError(null);
      return;
    }
    try {
      await verifyCodeInsee(value);
      setError(null);
    } catch (erreur: unknown) {
      setError(readError(erreur) as string);
    }
  }, 300);

  return (
    <Box sx={{ width: props.fullWidth ? "100%" : "300px" }}>
      <MaskInputConform
        label={label ?? t("contrat.infosContrat.lieuNaissance")}
        mask={RIE_LieuNaissance_mask}
        onChange={(e) => {
          debouncedValidateCodeInsee(e.target.value);
          if (onChange) {
            onChange(e);
          }
        }}
        field={field}
        showErrors={Boolean(!error)}
        {...props}
      />
      {error && <ErrorParagraph error={error} sx={{ mt: 0.375, ml: 1.75 }} inputFieldError />}
    </Box>
  );
};
